/**
 * Low credits (credits wave 2 F8 §3.4, W4 §3): when the paying wallet is under its low-water mark, a
 * strip above the chat input says what is left and about how many videos that makes. A member whose
 * org wallet pays is told to ask their admin instead of buying. Dismissed for the browser session;
 * best-effort like PlanBadge.
 */

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { billingAPI, type WalletBalance } from '../../services/api';
import { BuyCreditsDialog } from './BuyCreditsDialog';
import { videosFor } from '../../config/pricing';
import { studioBOn } from '../../lib/studioB/flag';

const DISMISS_KEY = 'low_credits_dismissed';

export function LowCreditsBanner({ refreshKey }: { refreshKey?: string | null }) {
  const [balance, setBalance] = useState<WalletBalance | null>(null);
  const [orgName, setOrgName] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === '1');
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    billingAPI.getBalance().then((b) => {
      if (cancelled) return;
      setBalance(b);
      if (b.scope === 'org') {
        billingAPI.getEntitlement().then((e) => { if (!cancelled) setOrgName(e?.paidBy?.name ?? null); }).catch(() => {});
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [refreshKey]);

  if (dismissed || !balance || !balance.lowWater) return null;

  const availableMilli = Math.max(0, balance.availableMilli);
  const left = Number((availableMilli / 1000).toFixed(2));
  const videos = videosFor(availableMilli / 1000);
  const org = balance.scope === 'org';
  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  const words = org
    ? `${orgName ?? 'Your organization'} has ${left} credits left; ask your admin.`
    : `${left} credits left${videos >= 1 ? ` · about ${videos} video${videos === 1 ? '' : 's'}` : ''}`;

  if (studioBOn()) {
    return (
      <div data-low-credits className="flex items-center gap-2.5 rounded-[var(--b-radius-card)] border border-b-line bg-b-panel py-1.5 pl-3.5 pr-1.5 text-b-meta text-b-t2">
        <span data-mark aria-hidden className="h-2 w-2 flex-none rounded-full bg-amber-500" />
        <span className="min-w-0 flex-1">{words}</span>
        {!org && (
          <button type="button" onClick={() => setBuying(true)} className="inline-flex h-7 flex-none items-center rounded-[var(--b-radius-control)] bg-b-ink px-2.5 text-b-control text-b-on-ink hover:opacity-90">
            Buy credits
          </button>
        )}
        <button type="button" onClick={dismiss} aria-label="Dismiss" className="grid h-7 w-7 flex-none place-items-center rounded-full text-b-t3 hover:bg-b-raised hover:text-b-t1">
          <X className="h-3.5 w-3.5" />
        </button>
        {!org && <BuyCreditsDialog open={buying} onOpenChange={setBuying} />}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
      <span className="flex-1">
        {org
          ? `${orgName ?? 'Your organization'} has ${left} credits left; ask your admin.`
          : `${left} credits left${videos >= 1 ? ` · about ${videos} video${videos === 1 ? '' : 's'}` : ''}`}
      </span>
      {!org && (
        <button type="button" onClick={() => setBuying(true)} className="rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground hover:opacity-90">
          Buy
        </button>
      )}
      <button type="button" onClick={dismiss} aria-label="Dismiss" className="text-muted-foreground hover:text-foreground">
        <X className="h-4 w-4" />
      </button>
      {!org && <BuyCreditsDialog open={buying} onOpenChange={setBuying} />}
    </div>
  );
}
